// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Customer autocomplete component module providing advanced customer selection functionality.
 * Built on top of Material-UI's Autocomplete component with custom rendering and behavior.
 */
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { Customer } from '@/customer/api/types';
import {
  Autocomplete,
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { omit } from 'lodash-es';
import { ForwardedRef, HTMLAttributes, forwardRef } from 'react';

/**
 * Type representing the minimal customer data needed for the autocomplete.
 * Includes only essential fields to keep the component lightweight.
 */
type CustomerOption = Pick<
  Customer,
  'id' | 'name' | 'isArchived' | 'isCustomer' | 'isSupplier'
>;

/**
 * Special ID used to identify the "create new customer" option.
 * This distinguishes the creation option from regular customer entries.
 */
export const CREATE_CUSTOMER_OPTION_ID = 'create-company';

/**
 * Determines if two customer options represent the same customer.
 * Used by the Autocomplete component for selection and value comparison.
 *
 * @param option - A customer option from the dropdown list
 * @param value - The currently selected customer value
 * @returns True if the options represent the same customer
 */
function isOptionEqualToValue(option: CustomerOption, value: CustomerOption) {
  return option.id === value.id;
}

/**
 * Renders a single customer option in the dropdown list.
 * Handles both regular customer entries and the special "create" option.
 *
 * Features:
 * - Custom styling for archived customers
 * - Special rendering for the create option
 * - Proper handling of HTML attributes
 *
 * @param attributes - HTML attributes for the list item
 * @param option - Customer option to render
 * @returns Rendered list item for the dropdown
 */
function renderOption(
  attributes: HTMLAttributes<HTMLLIElement>,
  option: CustomerOption
) {
  // Ensure that 'key' is not spread onto the list item element
  const props = omit(attributes, 'key');

  // Special rendering for the create new customer option
  if (option.id === CREATE_CUSTOMER_OPTION_ID) {
    return (
      <li {...props} key={option.id}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AddRounded fontSize="small" color="primary" />
          <Typography color="primary.main">
            Add company <b>{option.name}</b>
          </Typography>
        </Stack>
      </li>
    );
  }

  // Regular customer option with archived state handling and role chips
  const color = option.isArchived ? 'text.secondary' : 'text.primary';

  return (
    <li {...props} key={option.id}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        width="100%"
        justifyContent="space-between"
      >
        <Typography color={color}>
          {option.name} {option.isArchived && '(Archived)'}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Chip
            label="Customer"
            size="small"
            color="secondary"
            variant={option.isCustomer ? 'filled' : 'outlined'}
          />
          <Chip
            label="Supplier"
            size="small"
            color="secondary"
            variant={option.isSupplier ? 'filled' : 'outlined'}
          />
        </Stack>
      </Stack>
    </li>
  );
}

/**
 * Extracts the display label from a customer option.
 * Handles both string inputs (for free solo mode) and customer objects.
 *
 * @param option - Customer option or string input
 * @returns Display label for the option
 */
function getOptionLabel<FreeSolo extends boolean | undefined>(
  option: CustomerOption | AutocompleteFreeSoloValueMapping<FreeSolo>
) {
  if (typeof option === 'string') {
    return option;
  }

  return option.name;
}

/**
 * Props for the CustomerAutocomplete component.
 * Extends Material-UI's Autocomplete props with additional configuration.
 */
interface Props<
  Option extends CustomerOption,
  FreeSolo extends boolean | undefined,
> extends Omit<
    AutocompleteProps<Option, false, boolean, FreeSolo>,
    'renderInput' | 'isOptionEqualToValue' | 'getOptionLabel' | 'renderOption'
  > {
  /**
   * Name attribute for the form input element.
   * Used for form integration and accessibility.
   */
  name?: string;

  /**
   * Label text displayed above the input field.
   * Provides context for the search field.
   */
  label?: string;

  /**
   * Controls the error state of the component.
   * When true, displays error styling.
   */
  error?: boolean;

  /**
   * Text displayed below the input field.
   * Can be used for hints, validation messages, or error details.
   */
  helperText?: string;

  /**
   * Placeholder text shown in the empty input field.
   * Provides guidance on expected input.
   */
  placeholder?: string;
}

/**
 * Base renderer for the CustomerAutocomplete component.
 * Combines Material-UI's Autocomplete with custom rendering and behavior.
 *
 * Features:
 * - Custom input rendering with error states
 * - Forwarded ref support
 * - Form integration capabilities
 * - Customizable placeholder and helper text
 *
 * @template Option - Type of customer option (must include id, name, isArchived)
 * @template FreeSolo - Whether free text input is allowed
 */
function CustomerAutocompleteRenderer<
  Option extends CustomerOption,
  FreeSolo extends boolean | undefined,
>(props: Props<Option, FreeSolo>, ref: ForwardedRef<HTMLInputElement>) {
  const { name, label, placeholder, error, helperText, ...autocompleteProps } =
    props;

  /**
   * Renders the input field with proper form integration.
   * Handles ref forwarding and error states.
   */
  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...parameters}
      />
    );
  };

  return (
    <Autocomplete
      {...autocompleteProps}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
}

/**
 * A customized autocomplete component for selecting customers.
 * Provides advanced features like customer creation and archived state handling.
 *
 * Features:
 * - Customer search and selection
 * - Create new customer option
 * - Archived customer handling
 * - Form integration
 * - Error state support
 * - Ref forwarding
 *
 * @example
 * ```tsx
 * <CustomerAutocomplete
 *   options={customers}
 *   value={selectedCustomer}
 *   onChange={handleChange}
 *   label="Select customer"
 *   error={hasError}
 *   helperText={errorMessage}
 * />
 * ```
 */
export const CustomerAutocomplete = forwardRef(CustomerAutocompleteRenderer);
